/**
 * SlotMachine — refined game UX (PRD §18, post-audit overhaul).
 *
 * Audit fixes: real machine chrome (marquee lights + payline + cabinet),
 * prize SYMBOLS instead of truncated "Lime …" text, win flash + celebration,
 * i18n labels (no emoji). Winner decided upfront via weighted `pickPrize`; all
 * three reels land on the same prize so the payline reads as a match.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/lib/i18n/useT';
import type { GamePlayProps, WheelItem } from './pickPrize';
import { pickPrize } from './pickPrize';
import { GameStage } from './_shared/GameStage';
import { GameButton } from './_shared/GameButton';
import { PrizeSymbol } from './_shared/PrizeSymbol';
import { useGameSound } from './_shared/useGameSound';
import { useHaptics } from './_shared/useHaptics';
import { useCelebration } from './_shared/Celebration';
import { usePrefersReducedMotion } from './_shared/usePrefersReducedMotion';
import { shade, tint, withAlpha, readableText } from './_shared/colors';

const ROW_H = 78;
const VISIBLE_ROWS = 3;
const REELS = 3;

function buildStrip(items: WheelItem[], length = 40): WheelItem[] {
  const out: WheelItem[] = [];
  for (let i = 0; i < length; i++) out.push(items[i % items.length]);
  return out;
}

interface ReelProps {
  strip: WheelItem[];
  targetIdx: number;
  spinning: boolean;
  delayMs: number;
  duration: number;
  primary: string;
  accent: string;
  onStop?: () => void;
}

function Reel({ strip, targetIdx, spinning, delayMs, duration, primary, accent, onStop }: ReelProps) {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!spinning) return;
    const target = (strip.length - 5 + targetIdx) * ROW_H;
    setOffset(0);
    const start = performance.now();
    const total = duration + delayMs;
    let raf = 0;
    let stopped = false;
    const tick = (now: number) => {
      const elapsed = now - start;
      const tt = Math.min(elapsed / total, 1);
      const eased = 1 - Math.pow(1 - tt, 3);
      setOffset(target * eased);
      if (tt < 1) raf = requestAnimationFrame(tick);
      else if (!stopped) {
        stopped = true;
        onStop?.();
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinning, strip.length, targetIdx, delayMs, duration]);

  return (
    <div
      className="relative overflow-hidden rounded-xl"
      style={{
        width: 86,
        height: ROW_H * VISIBLE_ROWS,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(232,232,240,0.96))',
        boxShadow: `inset 0 8px 12px -6px ${withAlpha('#000', 0.4)}, inset 0 -8px 12px -6px ${withAlpha('#000', 0.4)}`,
        border: `2px solid ${shade(primary, 0.6)}`,
      }}
    >
      <div
        className="absolute left-0 right-0 will-change-transform"
        style={{ transform: `translateY(${-offset + ROW_H}px)` }}
      >
        {strip.map((p, i) => (
          <div key={i} className="flex items-center justify-center" style={{ height: ROW_H }}>
            <PrizeSymbol item={p} index={i} size={50} primary={primary} accent={accent} />
          </div>
        ))}
      </div>
      {/* Top/bottom fade */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `linear-gradient(180deg, ${withAlpha('#0a0a12', 0.35)} 0%, transparent 22%, transparent 78%, ${withAlpha('#0a0a12', 0.35)} 100%)`,
        }}
      />
    </div>
  );
}

export function SlotMachine({ items, primary, accent, onWin }: GamePlayProps) {
  const t = useT();
  const sound = useGameSound();
  const { vibrate } = useHaptics();
  const celebration = useCelebration();
  const reduced = usePrefersReducedMotion();

  const valid = useMemo(() => items.filter((i) => i.available > 0), [items]);
  const strip = useMemo(() => buildStrip(valid), [valid]);
  const [spinning, setSpinning] = useState(false);
  const [target, setTarget] = useState(0);
  const [won, setWon] = useState(false);
  const winnerRef = useRef<WheelItem | null>(null);

  const duration = reduced ? 700 : 2200;

  function pull() {
    if (spinning || valid.length === 0) return;
    const winnerIdx = pickPrize(valid);
    if (winnerIdx < 0) return;
    winnerRef.current = valid[winnerIdx];
    setTarget(winnerIdx);
    setWon(false);
    setSpinning(true);
    sound.play('spinUp');
    vibrate('bump');
    setTimeout(
      () => {
        setSpinning(false);
        setWon(true);
        celebration.fire();
        sound.play('win');
        vibrate('win');
        if (winnerRef.current) onWin(winnerRef.current);
      },
      duration + (reduced ? 200 : 400) * (REELS - 1) + 250,
    );
  }

  if (valid.length === 0) {
    return (
      <div className="rounded-2xl bg-black/40 p-8 text-center">
        <p className="font-bold">{t('publicSurvey.wheel.emptyTitle')}</p>
        <p className="mt-1 text-sm opacity-70">{t('publicSurvey.wheel.emptyBody')}</p>
      </div>
    );
  }

  return (
    <GameStage
      primary={primary}
      accent={accent}
      sound={sound}
      celebration={celebration}
      footer={
        <GameButton accent={accent} busy={spinning} onClick={pull}>
          {spinning ? t('publicSurvey.games.slots.busy') : t('publicSurvey.games.slots.action')}
        </GameButton>
      }
    >
      <div
        className="relative rounded-2xl p-4"
        style={{
          background: `linear-gradient(180deg, ${tint(primary, 0.1)}, ${shade(primary, 0.55)})`,
          boxShadow: `inset 0 1px 0 ${withAlpha('#fff', 0.25)}, 0 10px 24px -12px ${withAlpha('#000', 0.8)}`,
          border: `2px solid ${withAlpha('#fff', 0.12)}`,
        }}
      >
        {/* Marquee lights */}
        <div className="mb-3 flex items-center justify-center gap-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <span
              key={i}
              className="block h-2 w-2 rounded-full"
              style={{
                background: i % 2 === 0 ? accent : tint(accent, 0.4),
                boxShadow: `0 0 6px ${withAlpha(accent, 0.9)}`,
                animation: spinning ? `game-glow-pulse 0.6s ${i * 0.08}s ease-in-out infinite` : undefined,
                opacity: spinning ? 1 : 0.5,
              }}
            />
          ))}
        </div>

        <div className="relative flex gap-2">
          {Array.from({ length: REELS }, (_, i) => (
            <Reel
              key={i}
              strip={strip}
              targetIdx={target}
              spinning={spinning}
              delayMs={i * (reduced ? 200 : 400)}
              duration={duration}
              primary={primary}
              accent={accent}
              onStop={() => {
                sound.play('tick');
                vibrate('tick');
              }}
            />
          ))}
          {/* Payline */}
          <div
            className="pointer-events-none absolute inset-x-1 top-1/2 -translate-y-1/2"
            style={{
              height: ROW_H,
              borderTop: `2px solid ${accent}`,
              borderBottom: `2px solid ${accent}`,
              boxShadow: won ? `0 0 18px ${withAlpha(accent, 0.8)}` : undefined,
              transition: 'box-shadow 200ms',
            }}
          />
          <span
            className="pointer-events-none absolute top-1/2 -left-1 -translate-y-1/2"
            style={{ color: accent, filter: `drop-shadow(0 0 4px ${withAlpha(accent, 0.8)})` }}
            aria-hidden
          >
            ▶
          </span>
        </div>

        {/* Winner name */}
        <div className="mt-3 h-6 text-center">
          {won && winnerRef.current && (
            <span
              className="rounded-full px-3 py-0.5 text-sm font-extrabold"
              style={{ background: accent, color: readableText(accent), animation: 'game-pop-in 320ms both' }}
            >
              {winnerRef.current.name}
            </span>
          )}
        </div>
      </div>
    </GameStage>
  );
}
